import useSWR from 'swr';
import type { ScheduleRow } from './schema';
import type { ScheduleEntry } from './types';
import { rowToEntry } from './utils';

export const SCHEDULE_SWR_KEY = '/api/schedule';

interface ScheduleApiPayload {
  entries: ScheduleRow[];
}

async function fetchSchedule(): Promise<ScheduleEntry[]> {
  // No AbortController timeout here: in production the SW intercepts this
  // request and resolves from cache or network within its own 3s timeout
  // (see vite.config.ts runtimeCaching). A browser-side abort at 12s would
  // be dead code once the SW is installed and would create a confusing
  // dual-timeout policy.
  let response: Response;
  try {
    response = await fetch(SCHEDULE_SWR_KEY, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  } catch (e) {
    if (import.meta.env.DEV) {
      const { getDemoScheduleEntries } = await import('./utils');
      return getDemoScheduleEntries();
    }
    throw e;
  }

  if (!response.ok) {
    if (import.meta.env.DEV) {
      const { getDemoScheduleEntries } = await import('./utils');
      return getDemoScheduleEntries();
    }
    throw new Error(`/api/schedule returned ${response.status}`);
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new Error('/api/schedule returned non-JSON body');
  }

  const payload = raw as ScheduleApiPayload;
  if (!Array.isArray(payload.entries)) return [];
  return (payload.entries as ScheduleRow[]).map(rowToEntry);
}

export function useSchedule() {
  const { data, isLoading, error } = useSWR(SCHEDULE_SWR_KEY, fetchSchedule, {
    // Allow focus-based revalidation: a user returning to the browser on market
    // day should get a fresh schedule. The 5-minute dedupingInterval throttles
    // how often refetches actually hit the network.
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 5 * 60 * 1000, // 5 min
  });

  return {
    entries: data ?? [],
    isLoading,
    error: error as Error | undefined,
  };
}
